/**
 * Campaign payloads may carry `senderId` as a scalar or as `{ label, value }` (same shape as Select options).
 * @returns {{ id: string|number|null, label: string }}
 */
export function parseCampaignSenderIdField(raw) {
    if (raw === undefined || raw === null || raw === '') {
        return { id: null, label: '' };
    }
    if (typeof raw === 'object') {
        const id = raw.value ?? raw.senderId ?? raw.id;
        const label = raw.label ?? raw.senderEmail ?? '';
        const idNorm = id !== undefined && id !== null && id !== '' ? id : null;
        return {
            id: idNorm,
            label: typeof label === 'string' ? label : '',
        };
    }
    return { id: raw, label: '' };
}

/**
 * Maps provider sender API rows to Select options.
 * Expects `id`, `senderEmail`, `name`, and optional default flag (`isDefault` / `default` / `defaultSender`).
 */
function mapSenderItem(item, index) {
    const id = item.id ?? item.senderId ?? item.value;
    const email = item.senderEmail ?? '';
    const name = item.name ?? '';
    const isDefault = Boolean(item.isDefault ?? item.default ?? item.defaultSender);

    return {
        value: id !== undefined && id !== null ? String(id) : String(index),
        label: email,
        subLabel: name,
        isDefault,
    };
}

/**
 * Normalizes listSenders (or equivalent) API payloads to a sender row array.
 * @param {unknown} data
 * @returns {object[]}
 */
export function getMarketingSendersRawList(data) {
    if (data === undefined || data === null) {
        return [];
    }
    const nested = Array.isArray(data) ? data : (data?.items ?? data?.senders ?? data?.data);
    return Array.isArray(nested) ? nested : [];
}

export function normalizeMarketingSendersResponse(data) {
    const raw = getMarketingSendersRawList(data);
    return raw.map((item, index) => mapSenderItem(item, index));
}

/**
 * @param {unknown} row
 * @returns {string|null}
 */
export function getMarketingSenderIdFromRow(row) {
    if (!row || typeof row !== 'object') {
        return null;
    }
    const nested = row.data ?? row.result ?? row.sender;
    const source = nested && typeof nested === 'object' && !Array.isArray(nested) ? nested : row;
    const id = source.id ?? source.senderId ?? source.value;
    if (id === undefined || id === null || id === '') {
        return null;
    }
    return String(id);
}

/**
 * API row for Select merge (handles nested create/list payloads).
 * @param {unknown} row
 * @returns {object|null}
 */
export function getMarketingSenderRowFromApiResponse(row) {
    if (!row || typeof row !== 'object') {
        return null;
    }
    const nested = row.data ?? row.result ?? row.sender;
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
        return nested;
    }
    return row;
}

/**
 * @param {object[]} options
 * @param {string|number|null|undefined} preferredId
 * @returns {string}
 */
export function resolveSelectedSenderIdFromOptions(options, preferredId) {
    const list = Array.isArray(options) ? options : [];
    const validIds = new Set(list.map((option) => String(option.value)));
    if (preferredId != null && preferredId !== '' && validIds.has(String(preferredId))) {
        return String(preferredId);
    }
    const fallback = list.find((option) => option.isDefault) || list[0];
    return fallback ? String(fallback.value) : '';
}

/**
 * @param {object[]} options
 * @param {Array<{ row?: object, id?: string|number, label?: string, subLabel?: string }>} entries
 * @returns {object[]}
 */
export function mergeEnsureSendersIntoSelectOptions(options, entries) {
    const list = Array.isArray(options) ? options : [];
    const ensureList = Array.isArray(entries) ? entries : [];
    return ensureList.reduce((nextOptions, entry) => {
        const id = getMarketingSenderIdFromRow(entry?.row) ?? entry?.id;
        if (id == null || id === '') {
            return nextOptions;
        }
        if (nextOptions.some((option) => String(option.value) === String(id))) {
            return nextOptions;
        }

        const row = getMarketingSenderRowFromApiResponse(entry?.row);
        if (row) {
            const [option] = normalizeMarketingSendersResponse([row]);
            if (option) {
                return [...nextOptions, option];
            }
        }

        if (entry?.label) {
            return [
                ...nextOptions,
                {
                    value: String(id),
                    label: entry.label,
                    subLabel: entry.subLabel || '',
                    isDefault: false,
                },
            ];
        }

        return nextOptions;
    }, list);
}
